import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  CheckCircle2, Loader2, AlertTriangle, FileText, Pen, RotateCcw,
  ShieldCheck, Clock, Building2, Download,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function SignaturePad({ value, onChange, disabled }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const evt = e.touches?.[0] || e;
    return {
      x: (evt.clientX - rect.left) * (canvas.width / rect.width),
      y: (evt.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = getPos(e);
  };
  const move = (e) => {
    if (!drawingRef.current || disabled) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.strokeStyle = '#1e3a8a';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastRef.current = pos;
  };
  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    onChange?.(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    onChange?.('');
  };

  useEffect(() => {
    // Pré-remplit le canvas si une signature existe déjà
    if (!value || !canvasRef.current) return;
    const img = new Image();
    img.onload = () => {
      const ctx = canvasRef.current.getContext('2d');
      ctx.drawImage(img, 0, 0);
    };
    img.src = value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="border-2 border-dashed border-blue-300 rounded-lg overflow-hidden bg-white">
      <canvas
        ref={canvasRef}
        width={760}
        height={220}
        className="w-full block cursor-crosshair touch-none"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
        data-testid="signature-canvas"
      />
      <div className="flex items-center justify-between px-3 py-2 bg-slate-50 text-xs">
        <span className="text-slate-500 flex items-center gap-1">
          <Pen className="h-3 w-3" />
          Signe avec ton doigt ou ta souris dans le cadre
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={clear}
          disabled={disabled}
          className="text-slate-600 gap-1"
          data-testid="signature-clear"
        >
          <RotateCcw className="h-3 w-3" /> Effacer
        </Button>
      </div>
    </div>
  );
}

export default function SignContract() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [signatureB64, setSignatureB64] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);
  const [agree, setAgree] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/signature/${token}`);
      setData(data);
      if (data.status === 'signed') setSigned(true);
      if (data.client_name) setSignerName(data.client_name);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Lien invalide ou expiré');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submit = async () => {
    if (!signerName || signerName.trim().length < 3) {
      toast.error('Renseignez votre nom complet');
      return;
    }
    if (!signatureB64) {
      toast.error('Signez dans le cadre avant de valider');
      return;
    }
    if (!agree) {
      toast.error('Vous devez accepter les termes pour signer');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/signature/${token}/sign`, {
        signer_name: signerName.trim(),
        signature_b64: signatureB64,
      });
      setSigned(true);
      toast.success('Signature enregistrée avec succès !');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la signature');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 to-rose-50">
        <Card className="max-w-md w-full border-rose-200">
          <CardContent className="p-8 text-center space-y-3">
            <AlertTriangle className="h-12 w-12 mx-auto text-rose-500" />
            <h2 className="text-xl font-bold text-slate-800">Lien indisponible</h2>
            <p className="text-sm text-slate-600">{error}</p>
            <p className="text-xs text-slate-400">Contactez l'équipe SESAM si vous pensez qu'il s'agit d'une erreur.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (signed || data?.status === 'signed') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 to-emerald-50">
        <Card className="max-w-md w-full border-emerald-300">
          <CardContent className="p-8 text-center space-y-3" data-testid="signed-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="h-10 w-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800">Contrat signé !</h2>
            <p className="text-sm text-slate-600">
              Merci {data?.signer_name || signerName}. Votre signature a bien été enregistrée le{' '}
              {data?.signed_at ? new Date(data.signed_at).toLocaleString('fr-FR') : new Date().toLocaleString('fr-FR')}.
            </p>
            <p className="text-xs text-slate-500">
              Vous allez recevoir une copie du contrat signé par email d'ici quelques minutes.
            </p>
            <Badge className="bg-emerald-100 text-emerald-700 gap-1">
              <ShieldCheck className="h-3 w-3" />
              Signature électronique sécurisée
            </Badge>
          </CardContent>
        </Card>
      </div>
    );
  }

  const contrat = data?.contrat || {};
  const pdfUrl = `${API}/api/signature/${token}/pdf`;
  const fmt = (v) => Number(v || 0).toLocaleString('fr-FR').replace(/,/g, ' ');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-6 px-4">
      <div className="max-w-5xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-md">
            S
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Signature de contrat</h1>
            <p className="text-sm text-slate-500">CRM SESAM — Bangui, République Centrafricaine</p>
          </div>
        </div>

        {/* Récap contrat */}
        <Card className="border-blue-200">
          <CardContent className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-slate-500 uppercase font-semibold">Contrat</p>
              <p className="text-lg font-bold text-slate-800">{contrat.numero || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase font-semibold">Entreprise</p>
              <p className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-blue-600" />
                {contrat.entreprise || '—'}
              </p>
            </div>
            {contrat.objet && (
              <div className="md:col-span-2">
                <p className="text-xs text-slate-500 uppercase font-semibold">Objet</p>
                <p className="text-sm text-slate-700">{contrat.objet}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-slate-500 uppercase font-semibold">Période</p>
              <p className="text-sm text-slate-700">
                {contrat.date_debut?.slice(0, 10) || '—'} → {contrat.date_fin?.slice(0, 10) || '—'}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase font-semibold">Montant</p>
              <p className="text-xl font-bold text-emerald-600">{fmt(contrat.montant)} FCFA</p>
            </div>
          </CardContent>
        </Card>

        {/* Aperçu PDF */}
        <Card>
          <CardContent className="p-0">
            <div className="flex items-center justify-between p-3 bg-slate-100 border-b">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <FileText className="h-4 w-4 text-blue-600" />
                Contrat à signer
              </div>
              <a
                href={pdfUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                data-testid="download-contract-pdf"
              >
                <Download className="h-3 w-3" /> Télécharger le PDF
              </a>
            </div>
            <iframe
              src={pdfUrl}
              title="Contrat à signer"
              className="w-full"
              style={{ height: '600px' }}
            />
          </CardContent>
        </Card>

        {/* Bloc signature */}
        <Card className="border-2 border-blue-200">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Pen className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-bold text-slate-800">Votre signature</h2>
            </div>

            <div>
              <Label htmlFor="signer-name" className="text-sm">Nom complet du signataire *</Label>
              <Input
                id="signer-name"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Ex: Mamadou Konaté, Directeur Général"
                className="mt-1"
                data-testid="signer-name-input"
              />
            </div>

            <div>
              <Label className="text-sm">Tracez votre signature dans le cadre *</Label>
              <div className="mt-1">
                <SignaturePad value={signatureB64} onChange={setSignatureB64} />
              </div>
            </div>

            <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                className="mt-1"
                data-testid="agree-checkbox"
              />
              <span>
                Je reconnais avoir lu et accepté l'intégralité du contrat ci-dessus. En signant
                électroniquement, je m'engage de la même manière qu'avec une signature manuscrite,
                conformément au droit centrafricain et aux normes internationales (eIDAS).
              </span>
            </label>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2 border-t">
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Lien valide jusqu'au {data?.expires_at ? new Date(data.expires_at).toLocaleDateString('fr-FR') : '—'}
              </p>
              <Button
                onClick={submit}
                disabled={submitting || !signerName || !signatureB64 || !agree}
                className="bg-emerald-600 hover:bg-emerald-700 gap-2 px-6 h-11"
                data-testid="submit-signature"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                Signer le contrat
              </Button>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400">
          🔒 Vos données sont chiffrées et conservées en sécurité. Cette signature a la même valeur
          juridique qu'une signature manuscrite.
        </p>
      </div>
    </div>
  );
}
